import React, { FC, Suspense, useEffect, useMemo, useRef } from 'react';
import Loader from 'src/components/widgets/loader';
import OverlayLoading from 'src/components/widgets/overlayLoading';
import { Load3DSceneEditor } from 'src/components/widgets/preview3D';
import { loadFontsForDesign } from 'src/components/widgets/preview3D/loaders/designFontLoader';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import useComposerStore from '../../configurator-composer/composer/composerStore';
import { getComposerDefaultTemplate } from '../../configurator-composer/composer/helpers/composerDefaultTemplate';
import { ComposerTemplateNodeAdapter } from '../../configurator-composer/composer/helpers/composerNodesAdapter';
import styled from 'styled-components';
import ComposerViewportTools from './composerViewportTools';

const ComposerPreviewContainer = styled.div`
    display: flex;
    min-width: 0;
    min-height: 0;
    position: relative;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    /* Like the Scene Editor stage: no card under the model, but the same edge as the panels. */
    background: transparent;
    border: 1px solid var(--composer-line, #dbe2e6);
    border-radius: 16px;
    box-shadow: var(--composer-shadow, rgba(38, 50, 56, 0.06) 0 1px 2px);
    flex-grow: 1;
    height: 100%;
    overflow: hidden;
`;

// IL PALCO: il 3D e i suoi comandi sopra (`ComposerViewportTools`). È lui che va a schermo intero, così i
// comandi restano con il modello.
const ComposerStage = styled.div`
    position: relative;
    display: flex;
    min-width: 0;
    min-height: 0;
    height: 100%;
    flex-grow: 1;

    &:fullscreen {
        background: var(--composer-app, #f0f0f0);
    }
`;

// `tools={false}` dove il 3D è solo da guardare o dove il clic sul modello ha già un altro mestiere (i template).
const ComposerPreview: FC<{ tools?: boolean }> = ({ tools = true }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const stageRef = useRef<HTMLDivElement>(null);
    const [sceneEditor, sceneManager, designManager, zkProduct] = usePreview3DStore((x) => [x.sceneEditor, x.sceneManager, x.designManager, x.zkProduct]);
    const [templates, areas, selectedAreas] = useComposerStore((state) => [state.templates, state.areas, state.selectedAreas]);
    const renderedDesignRef = useRef(false);
    const previewGenerationRef = useRef(0);
    const previewRenderQueueRef = useRef<Promise<void>>(Promise.resolve());
    const variantReadyRef = useRef<Promise<void>>(Promise.resolve());
    const variantId = zkProduct?.get('colors').at(0).get('colorID');
    const defaultTemplate = getComposerDefaultTemplate(templates, zkProduct?.get('defaultDesignTemplateID'));
    const selectedSideID = selectedAreas?.[0]?.sideID;
    const previewModel = useMemo(() => {
        if (!zkProduct || !areas.length) return null;
        // Never mutate the product being saved. The Composer's area nodes may contain
        // unsaved sides and mesh links that only this in-memory preview needs.
        const model = new MPlaza.Model(JSON.parse(JSON.stringify(zkProduct)));
        const modelSides = new MPlaza.Sides();
        const meshSides = new MPlaza.ModelSceneMeshSides();
        // The 3D viewer currently resolves one side per mesh/submesh. Put the area
        // being edited first, so its placeholder is visible even on a shared mesh.
        const orderedAreas = [...areas].sort((a, b) => Number(b.sideID === selectedSideID) - Number(a.sideID === selectedSideID));
        orderedAreas.forEach((area) => {
            const { side, modelSides: areaMeshSides } = ComposerTemplateNodeAdapter.fromAreaNode({
                ...area,
                originalSide: new (MPlaza.Side as any)(JSON.parse(JSON.stringify(area.originalSide))) as MPlaza.Side,
                originalMeshSides: area.originalMeshSides?.map((meshSide) => new MPlaza.ModelSceneMeshSide(JSON.parse(JSON.stringify(meshSide))))
            });
            modelSides.add(side);
            areaMeshSides.forEach((meshSide) => meshSides.add(meshSide));
        });
        model.get('colors').at(0)?.set('sides', modelSides);
        model.get('scenes').at(0)?.set('meshSides', meshSides);
        return model;
    }, [zkProduct, areas, selectedSideID]);
    const updateColor = async () => {
        // We can't use the classic selectAttribteValue in ComposerSelection because it requires a fully constructed
        // composer selection with all attributes and options, but the variant attribute could be created on the fly
        const attributeSelection = sceneManager!.getSelectedAttributeOption(zkProduct!.get('colors').find((x) => x.get('colorID') === variantId)!);

        if (attributeSelection && attributeSelection.attributeGuid && attributeSelection.optionGuid) {
            const zkAttribute = zkProduct!.get('attributes').find((x) => x.get('attributeGuid') == attributeSelection.attributeGuid);

            if (zkAttribute) {
                const zkOption = zkAttribute.get('options').find((x) => x.get('optionGuid') == attributeSelection.optionGuid);

                if (zkOption) {
                    const actions = zkOption.get('actions').sortBy(function (item) {
                        return item.get('execOrder');
                    });

                    await sceneEditor!.executeActionsAsync(null, null, actions);
                    sceneEditor!.adjustCamera(false);
                } else throw `Option ${attributeSelection.optionGuid} not found`;
            } else throw `Attribute ${attributeSelection.attributeGuid} not found`;
        }

        setTimeout(() => designManager?.refreshSides(variantId!), 50);
    };

    useEffect(() => {
        if (containerRef.current && sceneEditor) {
            sceneEditor.appendTo(containerRef.current);
            sceneEditor.updateView(true, true);
            if (variantId) {
                variantReadyRef.current = updateColor().catch((error) => console.error('Error updating color:', error));
            }
        }
        console.log('ComposerPreview mounted', sceneEditor);
    }, [containerRef.current, sceneEditor]);

    useEffect(() => {
        if (!sceneEditor || !zkProduct) return;
        const generation = ++previewGenerationRef.current;
        const timer = window.setTimeout(() => {
            previewRenderQueueRef.current = previewRenderQueueRef.current
                .catch(() => {})
                .then(async () => {
                    if (generation !== previewGenerationRef.current) return;
                    await variantReadyRef.current;
                    if (generation !== previewGenerationRef.current) return;
                    const sourceDesign = defaultTemplate?.design;
                    if (!sourceDesign?.get('designItems').length || !previewModel) {
                        if (renderedDesignRef.current) {
                            sceneEditor.resetDesign();
                            sceneEditor.replaceModel(zkProduct);
                            renderedDesignRef.current = false;
                        }
                        return;
                    }
                    const design = new MPlaza.Design(JSON.parse(JSON.stringify(sourceDesign)));
                    design.setModel(previewModel);
                    await loadFontsForDesign(design);
                    if (generation !== previewGenerationRef.current) return;
                    // renderDesignAsync adds design overlays to the scene. Replace the previous
                    // preview first, otherwise every keystroke leaves stale text meshes behind.
                    sceneEditor.resetDesign();
                    sceneEditor.replaceModel(previewModel);
                    await sceneEditor.renderDesignAsync(design);
                    renderedDesignRef.current = true;
                })
                .catch((error) => {
                    if (generation === previewGenerationRef.current) {
                        sceneEditor.resetDesign();
                        sceneEditor.replaceModel(zkProduct);
                        renderedDesignRef.current = false;
                    }
                    console.error('Could not render the Composer design on the 3D preview:', error);
                });
        }, 250);
        return () => {
            window.clearTimeout(timer);
            previewGenerationRef.current++;
        };
    }, [sceneEditor, zkProduct, defaultTemplate?.design, previewModel]);

    return (
        <Suspense fallback={<Loader />}>
            <Load3DSceneEditor
                onProgress={() => null}
                fallback={<OverlayLoading forceShow />}
                damMode={false}
                isConfiguratorProduct={true}
                notLoadDesign={true}
            >
                <ComposerStage ref={stageRef}>
                    <ComposerPreviewContainer ref={containerRef}>
                        <Loader />
                    </ComposerPreviewContainer>
                    {tools && <ComposerViewportTools stageRef={stageRef} />}
                </ComposerStage>
            </Load3DSceneEditor>
        </Suspense>
    );
};

export default ComposerPreview;
